/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, UntypedFormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ErrorCodes, ToastDuration, ToastService } from '@shared';
import { LoggingService } from '@shared/logging';
import { debounceTime, Subscription } from 'rxjs';
import { FormControlFormula } from '../../controls/form-control-formula';
import { DotNetFormulaErrorCodesEnum } from '../../models/dotnet-formula-error-codes.enum';
import { DotNetFormulaResultModel } from '../../models/dotnet-formula-result.model';
import { DotNetFormulaDataProvider } from '../../services/dot-net-formula-data.provider';
import { RecalculateFormulaService } from '../../services/recalculate-formula.service';
import { NumberFormulaTypeHandler } from './formula-type-handlers/number-formula-type.handler';
import { StringFormulaTypeHandler } from './formula-type-handlers/string-formula-type.handler';
import { evaluateExpression } from './evaluate-expression';

declare const DotNet: any;

@Component({
	selector: 'app-formula-input',
	templateUrl: './formula-input.component.html',
	styleUrls: ['./formula-input.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FormulaInputComponent),
			multi: true
		}
	]
})
export class FormulaInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input() public dynamicFormControl: FormControlFormula;
	@Input() public form: UntypedFormGroup;

	public formulaResult: string | number;
	public formulaResultErrorMessage: string | null;
	private subscriptions: Subscription[] = [];

	public constructor(
		private recalculateFormulaService: RecalculateFormulaService,
		private dotNetFormulaDataProvider: DotNetFormulaDataProvider,
		private loggingService: LoggingService,
		private translate: TranslateService,
		private toastService: ToastService,
	) { }

	public writeValue(): void { }
	public registerOnChange(): void { }
	public registerOnTouched(): void { }

	public async ngOnInit() {

		this.formulaResult = this.dynamicFormControl.value !== undefined ? Number(this.dynamicFormControl.value) : undefined;
		console.log(this.formulaResult);
		this.subscriptions.push(
			/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
			this.recalculateFormulaService.recalculateFormula$.pipe(debounceTime(500)).subscribe(async (_: boolean) => {
				// todo: get updates only for columns needed in each formula
				let calculatedFormulaResult: DotNetFormulaResultModel;

				if (this.dynamicFormControl.formulaToCalculate) {

					try {
						if (this.form.value.status) {
							this.form.value.status = `"${this.form.value.status}"`;
						}

						calculatedFormulaResult = await DotNet.invokeMethodAsync(
							'blazormedium',
							'CalculateFormula',
							this.dotNetFormulaDataProvider.prepareColumnsData(this.form.value),
							this.dynamicFormControl.formulaToCalculate
						);
					} catch (error) {

						this.loggingService.error(ErrorCodes.DotNetCalculateFormulaError, error);
						// this.toastService.presentError(
						// 	this.translate.instant('formValidations.formulaNotSupported'),
						// 	ToastDuration.Fast
						// );
						throw error;

					}

					this.handleFormulaOnSuccess(calculatedFormulaResult);

					this.form.patchValue(
						{ [this.dynamicFormControl.key]: this.formulaResult },
						{ emitEvent: false, onlySelf: true }
					);
				}
			})
		);
	}

	public handleFormulaOnSuccess(calculatedFormulaResult: DotNetFormulaResultModel): void {
		const numberHandler = new NumberFormulaTypeHandler();
		const stringHandler = new StringFormulaTypeHandler();
		numberHandler.setNext(stringHandler);

		if (this.dynamicFormControl.key == "length") {
			this.dynamicFormControl.decimalPlaces = 2;
		}

		const formula = this.dynamicFormControl.formulaToCalculate;
		const dataRow = this.form.value;

		const columnTypes: Record<string, string> = {};
		for (const key in dataRow) {
			const safeKey = key.toLowerCase();
			let val: any = dataRow[key];
			const type = columnTypes[key] ?? columnTypes[safeKey] ?? 'CHAR';

			if (typeof val === 'string') {
				val = val.trim();
				if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
					val = val.slice(1, -1);
				}
			}

			if (type.toUpperCase().startsWith('CHAR')) {
				const maybeNumber = parseFloat(val);
				if (!isNaN(maybeNumber) && isFinite(maybeNumber)) {
					val = maybeNumber;
				}
			}

			dataRow[safeKey] = val;
		}

		const result = evaluateExpression(formula, dataRow, columnTypes);
		const finalResult = isNaN(Number(result)) ? result : this.roundValue(Number(result), this.dynamicFormControl.decimalPlaces);
		this.formulaResult = finalResult

		this.formulaResultErrorMessage = null;
	}

	public roundValue(value: number, decimalPlaces: number): number {
		const factor = Math.pow(10, decimalPlaces);
		return Math.round(value * factor) / factor;
	}

	public handleFormulaOnError(calculatedFormulaResult: DotNetFormulaResultModel): void {
		this.formulaResult = undefined;

		switch (calculatedFormulaResult.errorCode) {
			case DotNetFormulaErrorCodesEnum.NotInitialized:
			case DotNetFormulaErrorCodesEnum.FormulaIsEmptyOrNull:
			case DotNetFormulaErrorCodesEnum.NoColumnsOrValues:
			case DotNetFormulaErrorCodesEnum.FormulaExecutionFailed:
			case DotNetFormulaErrorCodesEnum.MiscellaneousError:
			case DotNetFormulaErrorCodesEnum.UnknownError:
			case DotNetFormulaErrorCodesEnum.FormulaExecutionFailedNaN:
				this.formulaResultErrorMessage = this.translate.instant('formValidations.formulaNotSupported');
				this.toastService.presentError(
					this.formulaResultErrorMessage,
					ToastDuration.Fast
				);
				break;
			case DotNetFormulaErrorCodesEnum.FormulaSyntaxIssue:
				this.formulaResultErrorMessage = this.translate.instant('formValidations.formulaFillRequiredFields');
				this.toastService.presentError(
					this.formulaResultErrorMessage,
					ToastDuration.Fast
				);
				break;
			default:
				this.formulaResultErrorMessage = null;
		}
	}

	public ngOnDestroy(): void {
		this.subscriptions.forEach((subscription) => {
			subscription.unsubscribe();
		});
	}
}
